import { c as _c } from "react/compiler-runtime";
import { getHostedUserAuthMethodsQueryKey } from '@onefootprint/fetch-hosted';
import type { ActionKind } from '@onefootprint/request-types';
import { Button, Form, PageShell } from '@onefootprint/ui';
import { createFileRoute, getRouteApi, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { UpdateVerify } from '@/idv2/features/auth-methods/update-verify';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'auth-methods-update-email'
});
const parentRoute = getRouteApi('/auth-methods');
const searchSchema = z.object({
  actionKind: z.enum(['add_primary', 'replace']).catch('add_primary'),
  currentEmail: z.string().optional()
});
export const Route = createFileRoute('/auth-methods/update-email')({
  component: UpdateEmail,
  validateSearch: searchSchema
});
type EmailFormData = {
  email: string;
};
function UpdateEmail() {
  const $ = _c(48);
  const navigate = useNavigate();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "authMethods"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t0);
  const {
    actionKind,
    currentEmail
  } = Route.useSearch();
  const {
    token
  } = parentRoute.useLoaderData();
  const {
    queryClient
  } = Route.useRouteContext();
  const [screen, setScreen] = useState(currentEmail ? "verify" : "collect");
  const [email, setEmail] = useState(currentEmail ?? "");
  let t1;
  if ($[1] !== email) {
    t1 = {
      defaultValues: {
        email
      }
    };
    $[1] = email;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const methods = useForm(t1);
  let t2;
  if ($[3] !== actionKind || $[4] !== t) {
    t2 = actionKind === "replace" ? t(_temp) : t(_temp2);
    $[3] = actionKind;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const title = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = data => {
      setEmail(data.email);
      setScreen("verify");
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleSubmit = t3;
  let t4;
  if ($[7] !== navigate || $[8] !== queryClient || $[9] !== token) {
    t4 = () => {
      logInfo("Email updated");
      queryClient.invalidateQueries({
        queryKey: getHostedUserAuthMethodsQueryKey({
          headers: {
            "X-Fp-Authorization": token
          }
        })
      });
      navigate({
        to: "/auth-methods"
      });
    };
    $[7] = navigate;
    $[8] = queryClient;
    $[9] = token;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleSuccess = t4;
  if (screen === "verify" && email) {
    const t5 = actionKind as ActionKind;
    let t6;
    if ($[11] !== email || $[12] !== handleSuccess || $[13] !== t5 || $[14] !== title || $[15] !== token) {
      t6 = <UpdateVerify token={token} challengeKind="email" actionKind={t5} contactValue={email} title={title} onSuccess={handleSuccess} />;
      $[11] = email;
      $[12] = handleSuccess;
      $[13] = t5;
      $[14] = title;
      $[15] = token;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    return t6;
  }
  let t5;
  if ($[17] !== t) {
    t5 = t(_temp3);
    $[17] = t;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== methods) {
    t6 = methods.handleSubmit(handleSubmit);
    $[19] = methods;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t) {
    t7 = t(_temp4);
    $[21] = t;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t) {
    t8 = t(_temp5);
    $[23] = t;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t) {
    t9 = t(_temp6);
    $[25] = t;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t9) {
    t10 = {
      required: t9
    };
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t7 || $[31] !== t8) {
    t11 = <Form.Email label={t7} name="email" placeholder={t8} rules={t10} />;
    $[29] = t10;
    $[30] = t7;
    $[31] = t8;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t) {
    t12 = t(_temp7);
    $[33] = t;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== t12) {
    t13 = <Button type="submit" variant="primary" fullWidth={true} size="large">{t12}</Button>;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t11 || $[38] !== t13 || $[39] !== t6) {
    t14 = <form onSubmit={t6} className="flex flex-col gap-4 items-start w-full" data-dd-privacy="mask">{t11}{t13}</form>;
    $[37] = t11;
    $[38] = t13;
    $[39] = t6;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== methods || $[42] !== t14) {
    t15 = <FormProvider {...methods}>{t14}</FormProvider>;
    $[41] = methods;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t15 || $[45] !== t5 || $[46] !== title) {
    t16 = <PageShell title={title} subtitle={t5}>{t15}</PageShell>;
    $[44] = t15;
    $[45] = t5;
    $[46] = title;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
}
function _temp7($_5) {
  return $_5.updateEmail.continue;
}
function _temp6($_4) {
  return $_4.updateEmail.requiredError;
}
function _temp5($_3) {
  return $_3.updateEmail.placeholder;
}
function _temp4($_2) {
  return $_2.updateEmail.label;
}
function _temp3($_1) {
  return $_1.updateEmail.subtitle;
}
function _temp2($_0) {
  return $_0.updateEmail.addTitle;
}
function _temp($) {
  return $.updateEmail.replaceTitle;
}